import * as React from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Tooltip, Rating,
  Divider, alpha, styled
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  ThumbUpOutlined as ThumbUpIcon,
  ThumbDownOutlined as ThumbDownIcon,
  Star as StarIcon,
  FavoriteBorder as HeartIcon,
  SentimentSatisfiedAlt as SatisfiedIcon,
  Bolt as BoltIcon
} from '@mui/icons-material';

// Styled premium Card with hover animations and glassmorphic shadow
const PremiumCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(224, 224, 224, 0.4)',
  background: 'linear-gradient(135deg, #ffffff 0%, #fcfcfd 100%)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'visible',
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 18px 40px rgba(0, 0, 0, 0.07)',
    borderColor: 'rgba(63, 81, 181, 0.15)',
  },
}));

// Helper to determine NPS color and labels
const getNpsConfig = (score: number) => {
  if (score >= 70) return { color: '#5DCC0A', label: 'Excelente', desc: 'Los clientes tienen alta probabilidad de recomendar el servicio de manera sobresaliente.' };
  if (score >= 30) return { color: '#8AD30A', label: 'Bueno', desc: 'Los clientes tienen alta probabilidad de recomendar el servicio.' };
  if (score >= 0) return { color: '#FFB400', label: 'Aceptable', desc: 'Los clientes están satisfechos pero podrían elegir otras opciones.' };
  return { color: '#FF4C51', label: 'Crítico', desc: 'Existe un alto riesgo de comentarios negativos de los detractores.' };
};

// Helper to determine CSAT color and label
const getCsatConfig = (score: number) => {
  if (score >= 80) return { color: '#5DCC0A', label: 'Excelente', desc: 'Los clientes se sienten altamente satisfechos con el servicio y la atención.' };
  if (score >= 60) return { color: '#FFB400', label: 'Aceptable', desc: 'Los clientes se sienten satisfechos con la atención, con oportunidades de mejora.' };
  return { color: '#FF4C51', label: 'Crítico', desc: 'Los clientes se sienten satisfechos con la atención.' };
};

// Helper to determine CES color and label
const getCesConfig = (score: number) => {
  if (score > 4) return { color: '#5DCC0A', label: 'Bajo Esfuerzo', desc: 'El proceso fue fácil y rápido para los clientes.' };
  if (score >= 3) return { color: '#FFB400', label: 'Esfuerzo Medio', desc: 'El proceso es manejable pero con algunas fricciones.' };
  return { color: '#FF4C51', label: 'Alto Esfuerzo', desc: 'El servicio fue difícil de utilizar para los clientes.' };
};

interface MonthlyMetricEntry {
  month: string;
  monthIndex: number;
  nps: { score: number; total: number; promoters: number; passives: number; detractors: number; } | null;
  csat: { score: number; total: number; positive: number; negative: number; } | null;
  ces: { score: number; total: number; sum: number; } | null;
}

interface RatingsMetricsSummaryProps {
  nps: {
    score: number;
    total: number;
    promoters: number;
    passives: number;
    detractors: number;
  } | number | null;
  csat: {
    score: number;
    total: number;
    positive: number;
    negative: number;
  } | number | null;
  ces: {
    score: number;
    total: number;
    sum: number;
  } | number | null;
  monthlyMetrics?: MonthlyMetricEntry[];
}

// Mini monthly metrics footer
const MonthlyMetricsFooter: React.FC<{
  data: MonthlyMetricEntry[];
  metricKey: 'nps' | 'csat' | 'ces';
  color: string;
}> = ({ data, metricKey, color }) => {
  const months = data.filter(m => m[metricKey] !== null);
  if (months.length === 0) return (
    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', display: 'block', textAlign: 'center' }}>
      Sin datos mensuales
    </Typography>
  );

  const getLabel = (entry: MonthlyMetricEntry) => {
    const m = entry[metricKey];
    if (!m) return '—';
    if (metricKey === 'nps') return `${(m as any).score > 0 ? '+' : ''}${(m as any).score}`;
    if (metricKey === 'csat') return `${(m as any).score}%`;
    if (metricKey === 'ces') return `${((m as any).score as number).toFixed(1)}`;
    return '—';
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      {months.map((entry) => (
        <Tooltip key={entry.monthIndex} title={`${entry.month}: ${getLabel(entry)}`} arrow placement="top">
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 44,
            px: 0.75,
            py: 0.5,
            borderRadius: '8px',
            bgcolor: alpha(color, 0.08),
            border: `1px solid ${alpha(color, 0.2)}`,
            cursor: 'default',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: alpha(color, 0.15) }
          }}>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1 }}>
              {entry.month.substring(0, 7)}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color, lineHeight: 1.4 }}>
              {getLabel(entry)}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

export const RatingsMetricsSummary: React.FC<RatingsMetricsSummaryProps> = ({ nps, csat, ces, monthlyMetrics = [] }) => {
  // Normalize NPS data
  const npsData = React.useMemo(() => {
    if (typeof nps === 'object' && nps !== null) {
      return {
        score: nps.score ?? 0,
        total: nps.total ?? 0,
        promoters: nps.promoters ?? 0,
        passives: nps.passives ?? 0,
        detractors: nps.detractors ?? 0
      };
    }
    return {
      score: typeof nps === 'number' ? nps : 0,
      total: 0,
      promoters: 0,
      passives: 0,
      detractors: 0
    };
  }, [nps]);

  // Normalize CSAT data
  const csatData = React.useMemo(() => {
    if (typeof csat === 'object' && csat !== null) {
      return {
        score: csat.score ?? 0,
        total: csat.total ?? 0,
        positive: csat.positive ?? 0,
        negative: csat.negative ?? 0
      };
    }
    return {
      score: typeof csat === 'number' ? csat : 0,
      total: 0,
      positive: 0,
      negative: 0
    };
  }, [csat]);

  // Normalize CES data
  const cesData = React.useMemo(() => {
    if (typeof ces === 'object' && ces !== null) {
      return {
        score: ces.score ?? 0,
        total: ces.total ?? 0,
        sum: ces.sum ?? 0
      };
    }
    return {
      score: typeof ces === 'number' ? ces : 0,
      total: 0,
      sum: 0
    };
  }, [ces]);

  // NPS Calculations
  const npsTotal = npsData.total;
  const promotersPct = npsTotal > 0 ? Math.round((npsData.promoters / npsTotal) * 100) : 0;
  const passivesPct = npsTotal > 0 ? Math.round((npsData.passives / npsTotal) * 100) : 0;
  const detractorsPct = npsTotal > 0 ? Math.round((npsData.detractors / npsTotal) * 100) : 0;
  const npsConfig = getNpsConfig(npsData.score);

  // CSAT radial progress variables
  const csatScore = Math.max(0, Math.min(100, csatData.score));
  const radius = 30;
  const strokeWidth = 5.5;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (csatScore / 100) * circumference;

  // CES configuration
  const cesConfig = getCesConfig(cesData.score);

  // CSAT configuration
  const csatConfig = getCsatConfig(csatData.score);

  return (
    <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
      {/* CARD NPS */}
      <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
        <PremiumCard sx={{ flexGrow: 1 }}>
          {/* Subtle colored accent top border */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bgcolor: npsConfig.color, borderRadius: '16px 16px 0 0' }} />

          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', flexGrow: 1 }}>
            <Box>
              {/* Header with Icon, Chip and Titles */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                {/* Circular Icon Container */}
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: alpha(npsConfig.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <HeartIcon sx={{ color: npsConfig.color, fontSize: '1.5rem' }} />
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: '6px',
                      bgcolor: npsConfig.color,
                      color: '#ffffff',
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      display: 'inline-block',
                      mb: 0.5
                    }}>
                      NPS
                    </Box>
                    <Tooltip
                      title="El NPS mide la lealtad y probabilidad de recomendación. va desde 1 a 5. Promotores (5), Pasivos (4), Detractores (1-3)."
                      arrow
                      placement="top"
                    >
                      <InfoIcon sx={{ color: 'text.disabled', cursor: 'help', fontSize: '1.15rem' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1rem', lineHeight: 1.2 }}>
                    Recomendación del Cliente
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.75rem', mt: 0.25 }}>
                    Net Promoter Score
                  </Typography>
                </Box>
              </Box>

              {/* Score and Status State */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: npsConfig.color, fontSize: '2.1rem', letterSpacing: '-0.75px', textShadow: `0 2px 10px ${alpha(npsConfig.color, 0.15)}` }}>
                  {npsData.score > 0 ? `+${npsData.score}` : npsData.score}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Estado: <span style={{ color: npsConfig.color, fontWeight: 800 }}>{npsConfig.label}</span>
                </Typography>
              </Box>

              {/* Description container */}
              <Box sx={{
                py: 1,
                px: 1.5,
                bgcolor: alpha(npsConfig.color, 0.08),
                borderRadius: '8px',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                  {npsConfig.desc}
                </Typography>
              </Box>

              {/* Segmented Progress Bar */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary' }}>Distribución de respuestas</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary' }}>Total: {npsTotal}</Typography>
                </Box>
                <Box sx={{ display: 'flex', height: 8, width: '100%', borderRadius: 4, overflow: 'hidden', bgcolor: '#F0F0F0', border: '1px solid #ECECEC' }}>
                  {npsTotal === 0 ? (
                    <Box sx={{ width: '100%', bgcolor: '#E0E0E0' }} />
                  ) : (
                    <>
                      {promotersPct > 0 && (
                        <Tooltip title={`Promotores: ${npsData.promoters} (${promotersPct}%)`} arrow>
                          <Box sx={{ width: `${promotersPct}%`, bgcolor: '#5DCC0A', transition: 'width 0.5s ease-in-out' }} />
                        </Tooltip>
                      )}
                      {passivesPct > 0 && (
                        <Tooltip title={`Pasivos: ${npsData.passives} (${passivesPct}%)`} arrow>
                          <Box sx={{ width: `${passivesPct}%`, bgcolor: '#FFB400', transition: 'width 0.5s ease-in-out' }} />
                        </Tooltip>
                      )}
                      {detractorsPct > 0 && (
                        <Tooltip title={`Detractores: ${npsData.detractors} (${detractorsPct}%)`} arrow>
                          <Box sx={{ width: `${detractorsPct}%`, bgcolor: '#FF4C51', transition: 'width 0.5s ease-in-out' }} />
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ my: 1.25, borderStyle: 'dashed' }} />

              {/* Sub-metrics labels */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tooltip title="Clientes altamente satisfechos y con alta probabilidad de recomendación." arrow placement="top">
                  <Box sx={{ textAlign: 'center', flex: 1, cursor: 'help' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#5DCC0A', fontWeight: 800, fontSize: '0.65rem' }}>Promotores</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{promotersPct}% <span style={{ fontSize: '0.65rem', color: '#888', fontWeight: 500 }}>({npsData.promoters})</span></Typography>
                  </Box>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title="Clientes satisfechos pero indiferentes, con riesgo de irse a la competencia." arrow placement="top">
                  <Box sx={{ textAlign: 'center', flex: 1, cursor: 'help' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#FFB400', fontWeight: 800, fontSize: '0.65rem' }}>Pasivos</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{passivesPct}% <span style={{ fontSize: '0.65rem', color: '#888', fontWeight: 500 }}>({npsData.passives})</span></Typography>
                  </Box>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title="Clientes con baja probabilidad de recomendación." arrow placement="top">
                  <Box sx={{ textAlign: 'center', flex: 1, cursor: 'help' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#FF4C51', fontWeight: 800, fontSize: '0.65rem' }}>Detractores</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{detractorsPct}% <span style={{ fontSize: '0.65rem', color: '#888', fontWeight: 500 }}>({npsData.detractors})</span></Typography>
                  </Box>
                </Tooltip>
              </Box>

              {/* Monthly Breakdown */}
              {monthlyMetrics.length > 0 && (
                <>
                  <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Por mes</Typography>
                  <MonthlyMetricsFooter data={monthlyMetrics} metricKey="nps" color={npsConfig.color} />
                </>
              )}
            </Box>
          </CardContent>
        </PremiumCard>
      </Grid>

      {/* CARD CSAT */}
      <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
        <PremiumCard sx={{ flexGrow: 1 }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bgcolor: '#3f51b5', borderRadius: '16px 16px 0 0' }} />

          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', flexGrow: 1 }}>
            <Box>
              {/* Header with Icon, Chip and Titles */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                {/* Circular Icon Container */}
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: alpha('#3f51b5', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <SatisfiedIcon sx={{ color: '#3f51b5', fontSize: '1.5rem' }} />
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: '6px',
                      bgcolor: '#3f51b5',
                      color: '#ffffff',
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      display: 'inline-block',
                      mb: 0.5
                    }}>
                      CSAT
                    </Box>
                    <Tooltip
                      title="El CSAT mide el porcentaje de opiniones positivas recibidas respecto al total de respuestas. Rango 0% a 100%."
                      arrow
                      placement="top"
                    >
                      <InfoIcon sx={{ color: 'text.disabled', cursor: 'help', fontSize: '1.15rem' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1rem', lineHeight: 1.2 }}>
                    Satisfacción del Cliente
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.75rem', mt: 0.25 }}>
                    Customer Satisfaction
                  </Typography>
                </Box>
              </Box>

              {/* Score and Status Level */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#3f51b5', fontSize: '2.1rem', letterSpacing: '-0.75px', textShadow: '0 2px 10px rgba(63, 81, 181, 0.15)' }}>
                  {csatData.score}%
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Nivel: <span style={{ color: csatConfig.color, fontWeight: 800 }}>{csatConfig.label}</span>
                </Typography>
              </Box>

              {/* Circular Progress Layout */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{
                    py: 1,
                    px: 1.5,
                    bgcolor: alpha(csatConfig.color, 0.08),
                    borderRadius: '8px',
                    mb: 1.5
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                      {csatConfig.desc}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem', fontWeight: 600 }}>
                    Respuestas recibidas: {csatData.total}
                  </Typography>
                </Box>

                {/* SVG radial progress (Smaller) */}
                <Box sx={{ position: 'relative', display: 'inline-flex', width: 72, height: 72, justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                  <svg width="72" height="72" viewBox="0 0 72 72">
                    {/* Track circle */}
                    <circle
                      cx="36"
                      cy="36"
                      r={radius}
                      fill="transparent"
                      stroke="#F0F0F0"
                      strokeWidth={4.5}
                    />
                    {/* Progress circle */}
                    <circle
                      cx="36"
                      cy="36"
                      r={radius}
                      fill="transparent"
                      stroke="#3f51b5"
                      strokeWidth={4.5}
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      style={{
                        transform: 'rotate(-90deg)',
                        transformOrigin: '36px 36px',
                        transition: 'stroke-dashoffset 0.8s ease-in-out'
                      }}
                    />
                  </svg>
                  {/* Center text */}
                  <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#3f51b5', fontSize: '0.78rem' }}>
                      {csatData.score}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ my: 1.25, borderStyle: 'dashed' }} />

              {/* Positive vs Negative Detail */}
              <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', p: 0.75, borderRadius: '50%', bgcolor: alpha('#5DCC0A', 0.1), color: '#5DCC0A' }}>
                    <ThumbUpIcon sx={{ fontSize: '0.95rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}>Positivas</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#5DCC0A' }}>{csatData.positive}</Typography>
                  </Box>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', p: 0.75, borderRadius: '50%', bgcolor: alpha('#FF4C51', 0.1), color: '#FF4C51' }}>
                    <ThumbDownIcon sx={{ fontSize: '0.95rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem' }}>Negativas</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#FF4C51' }}>{csatData.negative}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Monthly Breakdown */}
              {monthlyMetrics.length > 0 && (
                <>
                  <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Por mes</Typography>
                  <MonthlyMetricsFooter data={monthlyMetrics} metricKey="csat" color="#3f51b5" />
                </>
              )}
            </Box>
          </CardContent>
        </PremiumCard>
      </Grid>

      {/* CARD CES */}
      <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
        <PremiumCard sx={{ flexGrow: 1 }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', bgcolor: cesConfig.color, borderRadius: '16px 16px 0 0' }} />

          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', flexGrow: 1 }}>
            <Box>
              {/* Header with Icon, Chip and Titles */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                {/* Circular Icon Container */}
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: alpha(cesConfig.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <BoltIcon sx={{ color: cesConfig.color, fontSize: '1.5rem' }} />
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: '6px',
                      bgcolor: cesConfig.color,
                      color: '#ffffff',
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      display: 'inline-block',
                      mb: 0.5
                    }}>
                      CES
                    </Box>
                    <Tooltip
                      title="El CES mide la facilidad de interacción o servicio. 1 indica esfuerzo máximo y 5 indica esfuerzo mínimo (ideal)."
                      arrow
                      placement="top"
                    >
                      <InfoIcon sx={{ color: 'text.disabled', cursor: 'help', fontSize: '1.15rem' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1rem', lineHeight: 1.2 }}>
                    Facilidad del Servicio
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, fontSize: '0.75rem', mt: 0.25 }}>
                    Customer Effort Score
                  </Typography>
                </Box>
              </Box>

              {/* Score and Effort Level */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: cesConfig.color, fontSize: '2.1rem', letterSpacing: '-0.75px', textShadow: `0 2px 10px ${alpha(cesConfig.color, 0.15)}` }}>
                  {cesData.score.toFixed(1)} <span style={{ fontSize: '1rem', color: '#888', fontWeight: 600 }}>/ 5.0</span>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Nivel: <span style={{ color: cesConfig.color, fontWeight: 800 }}>{cesConfig.label}</span>
                </Typography>
              </Box>

              {/* Description container */}
              <Box sx={{
                py: 1,
                px: 1.5,
                bgcolor: alpha(cesConfig.color, 0.08),
                borderRadius: '8px',
                mb: 2
              }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                  {cesConfig.desc}
                </Typography>
              </Box>

              {/* Stars rating indicator */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                <Rating
                  value={cesData.score}
                  precision={0.1}
                  readOnly
                  emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                  icon={<StarIcon style={{ color: cesConfig.color }} fontSize="inherit" />}
                  sx={{ fontSize: '1.35rem' }}
                />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mt: 0.25, fontSize: '0.72rem' }}>
                  ({cesData.total} {cesData.total === 1 ? 'evaluación' : 'evaluaciones'})
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ my: 1.25, borderStyle: 'dashed' }} />

              {/* Dynamic Slider-like Indicator of Effort */}
              <Box sx={{ px: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary' }}>Distribución de esfuerzo percibido</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#FF4C51', fontWeight: 700, fontSize: '0.65rem' }}>Difícil (1)</Typography>
                  <Typography variant="caption" sx={{ color: '#FFB400', fontWeight: 700, fontSize: '0.65rem' }}>Medio (3)</Typography>
                  <Typography variant="caption" sx={{ color: '#5DCC0A', fontWeight: 700, fontSize: '0.65rem' }}>Fácil (5)</Typography>
                </Box>
                <Box sx={{ position: 'relative', height: 6, bgcolor: '#F0F0F0', borderRadius: 3, overflow: 'visible', mb: 0.5 }}>
                  {/* Gradient track */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRadius: 3,
                      background: 'linear-gradient(to right, #FF4C51, #FFB400 50%, #5DCC0A)'
                    }}
                  />
                  {/* Pointer showing current score */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: `${((cesData.score - 1) / 4) * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: '#ffffff',
                      border: `2px solid ${cesConfig.color}`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      transition: 'left 0.5s ease-in-out'
                    }}
                  />
                </Box>
              </Box>

              {/* Monthly Breakdown */}
              {monthlyMetrics.length > 0 && (
                <>
                  <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', color: 'text.secondary', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Por mes</Typography>
                  <MonthlyMetricsFooter data={monthlyMetrics} metricKey="ces" color={cesConfig.color} />
                </>
              )}
            </Box>
          </CardContent>
        </PremiumCard>
      </Grid>
    </Grid>
  );
};
